import { StrictMode, useState, useRef, useLayoutEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Router,
  UNSAFE_createBrowserHistory as createBrowserHistory,
} from 'react-router-dom';
import type { NavigationType } from 'react-router-dom';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import App from './App.tsx';
import store from './store/index.ts';
import { ThemeProvider } from "@material-tailwind/react";
import { setupGoogleServices } from './utils/analyticsSetup';
import { applyConsent } from './utils/consent';
import { initSentry } from './utils/sentry';

// Error tracking first: anything that throws after this point is captured,
// including failures in the setup below it.
initSentry();

// Setup Google Analytics
setupGoogleServices();


// Re-apply whatever was decided previously, on every load.
//
// Without this the stored decision was read only to decide whether to show the
// banner: a returning visitor who accepted got no analytics, and one who declined
// got no protection either, because index.html had already loaded the tag for
// everyone regardless.
applyConsent();
// 🔒 SECURITY: Completely disable console output
// (() => {
//   const noop = () => {};
// })();

// Custom BrowserRouter that uses synchronous state updates instead of
// React.startTransition(). React Router v7's default BrowserRouter wraps
// navigation state changes in startTransition, which allows heavy components
// (like UnifiedPlayerPage with video playback) to defer route transitions
// indefinitely. This causes the URL to change while the old page stays visible.
function SyncBrowserRouter({ children }: { children: React.ReactNode }) {
  const historyRef = useRef(createBrowserHistory({ v5Compat: true }));
  const history = historyRef.current;
  const [state, setState] = useState<{ action: NavigationType; location: typeof history.location }>({
    action: history.action as NavigationType,
    location: history.location,
  });

  useLayoutEffect(
    () => history.listen(({ action, location }) => {
      setState({ action: action as NavigationType, location });
    }),
    [history]
  );

  return (
    <Router
      location={state.location}
      navigationType={state.action}
      navigator={history}
    >
      {children}
    </Router>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <ThemeProvider>
        <Provider store={store}>
          <SyncBrowserRouter>
            <App />
          </SyncBrowserRouter>
        </Provider>
      </ThemeProvider>
    </HelmetProvider>
  </StrictMode>,
);